import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./ProductFilterPage.css";
import api from "../services/api";
import ProductLayout from "../components/Layouts/ProductLayout";
import { CloseIcon, SearchIcon } from "../components/Icons/icons";
import { Badge } from "react-bootstrap";

const ProductFilterPage = () => {
  // Filters
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");

  // Products
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Price Range
  const [MIN, setMIN] = useState(0);
  const [MAX, setMAX] = useState(0);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);

  // =========================
  // Optimized Category Filter
  // =========================
  const filteredCategories = useMemo(() => {
    return categoryList.filter((item) =>
      item.category_name
        .toLowerCase()
        .includes(categorySearch.toLowerCase())
    );
  }, [categoryList, categorySearch]);

  // =========================
  // Slider Handlers
  // =========================
  const handleMinChange = (e) => {
    const value = Number(e.target.value);
    if (value <= maxPrice) setMinPrice(value);
  };

  const handleMaxChange = (e) => {
    const value = Number(e.target.value);
    if (value >= minPrice) setMaxPrice(value);
  };

  // =========================
  // Load Categories + MinMax
  // =========================
  useEffect(() => {
    const loadData = async () => {
      try {
        const [catRes, priceRes] = await Promise.all([
          api.get("fetch-categories"),
          api.get("fetch-min-max-product"),
        ]);

        // categories
        setCategoryList(catRes.data || []);

        // price range
        const min = Number(priceRes.data.min_price) || 0;
        const max = Number(priceRes.data.max_price) || 0;

        setMIN(min);
        setMAX(max);
        setMinPrice(min);
        setMaxPrice(max);
      } catch (err) {
        console.error(err);
      }
    };

    loadData();
  }, []);

  // =========================
  // Fetch Products
  // =========================
  const fetchProducts = useCallback(async () => {
    if (MAX === 0) return;

    setLoading(true);

    try {
      const res = await api.get("fetch-products", {
        params: {
          search,
          category,
          min_price: minPrice,
          max_price: maxPrice,
        },
      });

      setProducts(res.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [search, category, minPrice, maxPrice, MAX]);

  // =========================
  // Debounced Fetch
  // =========================
  useEffect(() => {
    if (MAX === 0) return;

    const timer = setTimeout(() => {
      fetchProducts();
    }, 700);

    return () => clearTimeout(timer);
  }, [fetchProducts, MAX]);

  // =========================
  // UI
  // =========================
  return (
    <div className="container-fluid">
      <div className="row g-4">

        {/* ================= SIDEBAR ================= */}
        <div className="col-md-3">
          <div className="border rounded p-3 shadow-sm bg-white">

            {/* Category Search */}
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search Category..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
              />

              {categorySearch && (
                <span
                  className="input-group-text"
                  onClick={() => setCategorySearch("")}
                >
                  <CloseIcon width={20} height={20} fill="#f30f0f" />
                </span>
              )}
            </div>

            {/* Categories */}
            <div
              className="border rounded p-3 mb-4 overflow-auto"
              style={{ maxHeight: "200px" }}
            >
             {filteredCategories.map((item) => (
              <div className="form-check mb-2" key={item.id}>

                <input
                  className="form-check-input cursor-pointer"
                  type="radio"
                  name="category"
                  id={`category-${item.id}`}
                  checked={category === item.category_name}
                  onChange={() => setCategory(item.category_name)}
                />

                <label
                  className="form-check-label cursor-pointer"
                  htmlFor={`category-${item.id}`}
                >
                  {item.category_name}
                </label>

              </div>
            ))}
            </div>

            {/* Price Range */}
            <div className="text-center border rounded py-1 mb-3 fw-semibold">
              Price Range
            </div>

            <div className="px-2">

              {/* Slider */}
              <div className="range-slider position-relative">

                <input
                  type="range"
                  min={MIN}
                  max={MAX}
                  value={minPrice}
                  onChange={handleMinChange}
                  className="thumb thumb-left"
                />

                <input
                  type="range"
                  min={MIN}
                  max={MAX}
                  value={maxPrice}
                  onChange={handleMaxChange}
                  className="thumb thumb-right"
                />

                <div className="slider">
                  <div className="slider-track"></div>

                  <div
                    className="slider-range"
                    style={{
                      left:
                        MAX > MIN
                          ? `${((minPrice - MIN) / (MAX - MIN)) * 100}%`
                          : "0%",
                      right:
                        MAX > MIN
                          ? `${100 - ((maxPrice - MIN) / (MAX - MIN)) * 100}%`
                          : "0%",
                    }}
                  />
                </div>
              </div>

              {/* Inputs */}
              <div className="d-flex justify-content-between gap-2 mt-2">

                <input
                  type="number"
                  className="form-control text-center"
                  min={MIN}
                  max={maxPrice}
                  value={minPrice}
                  onChange={(e) =>
                    setMinPrice(
                      e.target.value === ""
                        ? ""
                        : Number(e.target.value)
                    )
                  }
                />

                <input
                  type="number"
                  className="form-control text-center"
                  min={minPrice}
                  max={MAX}
                  value={maxPrice}
                  onChange={(e) =>
                    setMaxPrice(
                      e.target.value === ""
                        ? ""
                        : Number(e.target.value)
                    )
                  }
                />

              </div>
            </div>
          </div>
        </div>

        {/* ================= PRODUCTS ================= */}
        <div className="col-md-9">

          {/* Search Bar */}
          <div className="border rounded p-2 mb-3 d-flex align-items-center">

            {category && (
              <Badge
                bg="light"
                className="d-flex align-items-center border text-primary me-2"
                style={{ cursor: "pointer" }}
              >
                {category}
                <span onClick={() => setCategory("")}>
                  <CloseIcon width={18} height={18} fill="#f30f0f" />
                </span>
              </Badge>
            )}

            <div className="position-relative w-100">
              <div className="d-flex align-items-center border rounded-pill px-2">

                <div className="mx-2">
                    <SearchIcon width={18} height={18} fill="#6c757d" />
                </div>

                <input
                  type="text"
                  className="form-control border-0 shadow-none"
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                {search && (
                  <span onClick={() => setSearch("")} className="mx-1">
                    <CloseIcon width={18} height={18} fill="#f30f0f" />
                  </span>
                )}

              </div>
            </div>

          </div>

          {/* Products */}
          <div className="border">
            <div className="row g-2">

              {loading ? (
                <div className="text-center py-5 w-100">
                  <div className="spinner-border text-primary"></div>
                  <p className="mt-2">Loading...</p>
                </div>
              ) : products.length > 0 ? (
                products.map((product) => (
                  <ProductLayout
                    key={product.id}
                    id={product.id}
                    image={product.product_primary_img?.image_file_name}
                    product_name={product.product_name}
                    slug_to_product={product.slug_to_product}
                    price={product.product_unit_price}
                    discount={product.product_discount}
                    after_discount_price={product.after_discount_price}
                    columnClass="col-12 col-sm-6 col-md-4 col-lg-3"
                  />
                ))
              ) : (
                <div className="text-center py-5 w-100">
                  <h5>No products found</h5>
                  <p>Try different filters</p>
                </div>
              )}

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ProductFilterPage;